/**
 * Debug logger utility - conditionally logs based on environment
 * 
 * Enable debug logs by setting VITE_DEBUG=true in .env or localStorage.setItem('debug', 'true')
 */

const isDebugEnabled = (): boolean => {
  // Check localStorage first (allows runtime toggling)
  if (typeof window !== 'undefined') {
    const localStorageDebug = localStorage.getItem('debug');
    if (localStorageDebug === 'true') return true;
    if (localStorageDebug === 'false') return false;
  }
  
  // Check environment variable
  return import.meta.env?.VITE_DEBUG === 'true' || import.meta.env?.DEV === true;
};

// Cache the value to avoid repeated checks
let debugEnabled: boolean | null = null;

const checkDebug = (): boolean => {
  if (debugEnabled === null) {
    debugEnabled = isDebugEnabled();
  }
  return debugEnabled;
};

/**
 * Debug log - only outputs in development or when debug is enabled
 */
export const debugLog = (message: string, ...args: unknown[]): void => {
  if (checkDebug()) {
    console.log(message, ...args);
  }
};

/**
 * Debug warn - only outputs in development or when debug is enabled
 */
export const debugWarn = (message: string, ...args: unknown[]): void => {
  if (checkDebug()) {
    console.warn(message, ...args);
  }
};

/**
 * Info log - always outputs (for important state changes)
 */
export const infoLog = (message: string, ...args: unknown[]): void => {
  console.log(message, ...args);
};

/**
 * Reset debug cache (useful when changing debug setting at runtime)
 */
export const resetDebugCache = (): void => {
  debugEnabled = null;
};

/**
 * Enable/disable debug at runtime
 */
export const setDebug = (enabled: boolean): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('debug', enabled ? 'true' : 'false');
    resetDebugCache();
  }
};
